import React from 'react';
import { useParams } from 'react-router';
import classNames from 'classnames';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import intersectionBy from 'lodash/intersectionBy';
import styled from '@emotion/styled';
import { token } from '@atlaskit/tokens';
import { ProxyGraphClients, abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import {
  ColGroup,
  CustomDynamicResultTable,
  errorNotificationBuilder,
  TableBody,
  TableHead,
  useNotification,
} from '@css-xp/base';
import { fetchNativeProductCatalogueWithOfferingAndPricingPlans } from '@css-xp/ppc-api';
import { emptyStateProp, errorStateProp } from '@css-xp/ppc-common';
import { ATLAS_PRODUCT_ID, CATALOG_PAGE_LOADER_OPTIONS, JIRA_PRODUCT_IDS } from '@css-xp/ppc-constants';
import { usePartnerCatalogueContext } from '../../context/partner-catalogue-context';
import { usePartnerProductCatalogueContext } from '../contexts/product-catalogue-context';
import { constructQuerySearchParams } from './products-search';
import { createHeadV2, createRowsV2 } from './products-table-items-v2';
import './products-table.scss';

const StyledTable = styled.div`
  .products-table-v2 {
    .products-list-table {
      .details-btn > button {
        padding: ${token('space.025')};
      }
    }
  }
`;

export const ProductsTable = ({
  isJWMDisabled,
  isAtlasDisabled,
  isEnterpriseOfferingEnabled,
  isJSMRepackagingEnabled,
  isCompassPremiumEnabled,
  isJPDAsJiraFamilyEnabled,
  version,
  isAppificationEnabled,
}: {
  isJWMDisabled?: boolean;
  isAtlasDisabled?: boolean;
  isEnterpriseOfferingEnabled?: boolean;
  isJSMRepackagingEnabled?: boolean;
  isCompassPremiumEnabled?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  version?: number;
  isAppificationEnabled?: boolean;
}) => {
  const [tableRows, setTableRows] = React.useState<PartnerProductCatalogueItem[]>([]);
  const [tableLevelLoading, setTableLevelLoading] = React.useState<boolean>(false);
  const isVersion2 = version === 2;
  const { txaId } = useParams<{ txaId: string }>();
  const {
    filterState,
    data: catalogueData,
    isTableLoading,
    searchResults,
    querySearch,
    setQuerySearch,
    existingCloudSiteProductIds,
  } = usePartnerProductCatalogueContext();

  const {
    quoteItems,
    setCatalogueItem,
    setIsPartnerCatalogueInformationLoading,
    setEstimatesToBeRefreshed,
    pricingPlanCycle,
  } = usePartnerCatalogueContext();
  const { notificationSent, sendErrorNotification, setNotificationSent } = useNotification();
  const filteredProducts = filterState?.products;
  const hasFilteredProducts = filteredProducts?.length > 0;
  // Note: Initially `searchResults` is undefined and changes to an array after searching
  const hasFilterOrSearchApplied = hasFilteredProducts || searchResults;
  const hasFilterAndSearchApplied = hasFilteredProducts && searchResults;
  let products: any = [];

  if (hasFilterAndSearchApplied) {
    products = intersectionBy(filteredProducts, searchResults, 'name');
  } else if (searchResults) {
    products = searchResults;
  } else if (hasFilteredProducts) {
    products = filteredProducts;
  }

  const catalogExcludedProducts: string[] = [];

  if (isAtlasDisabled) {
    catalogExcludedProducts.push(ATLAS_PRODUCT_ID);
  }

  if (isJWMDisabled) {
    // JWM product id
    catalogExcludedProducts.push(JIRA_PRODUCT_IDS.JWM);
  }

  const createTableHead = React.useCallback(() => {
    return createHeadV2(version, isAppificationEnabled);
  }, []);

  const createTableRows = React.useCallback(() => {
    const createRowsProps = {
      products: tableRows,
      quoteItems,
      setCatalogueItem,
      setIsPartnerCatalogueInformationLoading,
      txaId,
      setEstimatesToBeRefreshed,
      isRowClickable: true,
      existingCloudSiteProductIds,
      catalogExcludedProducts,
      isEnterpriseOfferingEnabled,
      isJSMRepackagingEnabled,
      isCompassPremiumEnabled,
      isJPDAsJiraFamilyEnabled,
      version,
      isAppificationEnabled,
    };
    return createRowsV2(createRowsProps);
  }, [
    setIsPartnerCatalogueInformationLoading,
    setCatalogueItem,
    quoteItems,
    tableRows,
    txaId,
    setEstimatesToBeRefreshed,
    existingCloudSiteProductIds,
    catalogExcludedProducts,
    isEnterpriseOfferingEnabled,
    isJSMRepackagingEnabled,
    isCompassPremiumEnabled,
    isJPDAsJiraFamilyEnabled,
    isAppificationEnabled,
  ]);

  React.useEffect(() => {
    //storing product data of default result
    catalogueData && setTableRows([...catalogueData?.nativeCatalog?.nodes]);
    const cursor = defaultTo(get(catalogueData, 'nativeCatalog.pageInfo.endCursor'), undefined);
    const hasNextPage = defaultTo(get(catalogueData, 'nativeCatalog.pageInfo.hasNextPage'), false);
    // Storing all the page params into product catalogue context since it is used in other page as well
    setQuerySearch(constructQuerySearchParams(cursor, hasNextPage, querySearch?.pageParams?.searchText));
  }, [catalogueData]);
  React.useEffect(() => {
    //storing product data of search result
    if (hasFilterOrSearchApplied) {
      setTableRows([...products]);
    }
  }, [hasFilterOrSearchApplied]);

  const onLoadMore = React.useCallback(async () => {
    const endCursor = defaultTo(get(querySearch, 'variables.pageParams.cursor'), undefined);

    if (endCursor) {
      let response;
      setTableLevelLoading(true);
      setQuerySearch({ ...querySearch, hasNextPage: false });
      try {
        response = await graphqlClient(ProxyGraphClients.SXP).query({
          query: fetchNativeProductCatalogueWithOfferingAndPricingPlans,
          variables: {
            ...querySearch.variables,
            pricingPlanFilterOptions: [
              {
                key: 'currency',
                value: 'USD',
              },
              {
                key: 'billingFrequency',
                value: pricingPlanCycle,
              },
            ],
            filterOptions: [
              {
                key: 'includeProperties',
                values: ['vendorName'],
              },
            ],
          },
        });
      } catch (err) {
        // Remove the loading state for the products table
        setTableLevelLoading(false);
        if (!notificationSent) {
          const notificationOptions = errorNotificationBuilder()
            .withId('product-load-more-failure')
            .withMessage(defaultTo(get(err, 'message'), ''))
            .withDismissAfter(5000)
            .build();
          sendErrorNotification(notificationOptions);
          setNotificationSent(true);
        }
        return;
      }

      const node = defaultTo(get(response, 'data.nativeCatalog.nodes'), []);
      const cursor = defaultTo(get(response, 'data.nativeCatalog.pageInfo.endCursor'), undefined);
      const hasNextPage = defaultTo(get(response, 'data.nativeCatalog.pageInfo.hasNextPage'), false);
      const searchText = defaultTo(get(querySearch, 'variables.searchText'), '');
      setTableRows([...tableRows, ...node]);
      setQuerySearch(constructQuerySearchParams(cursor, hasNextPage, searchText));
      setTableLevelLoading(false);
    }
  }, [tableRows, querySearch, setQuerySearch]);

  const renderProductsTable = () => {
    return (
      <div className={classNames({ 'products-table': !isVersion2 }, { 'products-table-v2': isVersion2 })}>
        <CustomDynamicResultTable
          className="products-list-table"
          loadingSpinnerSize="large"
          isLoading={isTableLoading}
          testId="product-catalog-list"
          error=""
          isPaginationEnabled={false}
          showLoadMore={querySearch?.hasNextPage && !isTableLoading}
          loadMoreText={'Load more'}
          onLoadMore={onLoadMore}
          loadMoreLoading={tableLevelLoading}
          noResults={tableRows.length === 0}
          boxShadow={false}
          emptyStateProps={emptyStateProp}
          errorStateProps={errorStateProp}
          {...(isVersion2
            ? {
                loaderOptions: { ...CATALOG_PAGE_LOADER_OPTIONS },
              }
            : {})}
        >
          <ColGroup columns={createTableHead()} />
          <TableHead columns={createTableHead()} />
          <TableBody>{createTableRows()}</TableBody>
        </CustomDynamicResultTable>
      </div>
    );
  };

  return isVersion2 ? <StyledTable>{renderProductsTable()}</StyledTable> : renderProductsTable();
};
